import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';

/**
 * Una captura del panel dentro de un marco que se endereza al scrollear.
 *
 * Arriba se ve inclinada hacia atrás, como una pantalla apoyada sobre un
 * escritorio; a medida que la sección pasa por la ventana se va parando hasta
 * quedar de frente. Es la manera de mostrar el producto sin pedirle a nadie que
 * se registre para verlo.
 *
 * Cómo se maneja el scroll: `useScroll` con `target` mide cuánto avanzó ESTA
 * sección por la ventana, no la página entera, así que la animación es
 * independiente de dónde esté ubicada.
 *
 * Convive con el scroll con inercia de la landing (hooks/useScrollSuave): Lenis
 * mueve el scroll real de la ventana, no un contenedor con transform, así que
 * `useScroll` lo lee sin necesidad de conectarlos.
 *
 * `perspective` va en el contenedor padre y no en la tarjeta: es lo que define
 * desde qué distancia se mira la escena. Puesta en la tarjeta, cada una sería
 * su propio punto de fuga y la inclinación se vería plana.
 */
export default function PanelEnPerspectiva({ encabezado, children }) {
  const contenedor = useRef(null);
  const menosMovimiento = useReducedMotion();
  const [esTelefono, setEsTelefono] = useState(false);

  // El recorrido se mide desde que la sección asoma por abajo hasta que su
  // centro llega al centro de la ventana. Sin este `offset` framer usa
  // ["start start", "end end"], que empieza a contar recién cuando el borde de
  // arriba de la sección toca el borde de arriba de la ventana: para entonces
  // la tarjeta ya está abajo de todo y hay que seguir scrolleando a ciegas para
  // encontrarla. Así en cambio la tarjeta se endereza justo mientras entra.
  const { scrollYProgress } = useScroll({
    target: contenedor,
    offset: ['start end', 'center center'],
  });

  useEffect(() => {
    const medir = () => setEsTelefono(window.innerWidth <= 768);
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, []);

  // En teléfono arranca más chica porque el giro sobre una tarjeta que ya ocupa
  // todo el ancho se come los bordes. Termina en 1 igual que en escritorio: si
  // se queda en 0.9 parece encogida al lado del resto de la página.
  const escala = useTransform(
    scrollYProgress,
    [0, 1],
    esTelefono ? [0.85, 1] : [1.05, 1]
  );
  const rotacion = useTransform(scrollYProgress, [0, 1], [20, 0]);
  // El encabezado sube un poco menos que antes (eran 100px). Con el recorrido
  // nuevo, más corto, 100px era un salto que se notaba más que el giro.
  const subida = useTransform(scrollYProgress, [0, 1], [0, -40]);

  // Quien pide menos movimiento ve la tarjeta derecha y quieta, sin scroll
  // atado a nada. Es la misma decisión que toma `Aparecer` en el resto de la
  // landing.
  if (menosMovimiento) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-16 sm:px-6">
        <div className="mx-auto max-w-5xl text-center">{encabezado}</div>
        <Marco className="mt-10">{children}</Marco>
      </div>
    );
  }

  return (
    /* La sección mide lo que mide su contenido más el aire de arriba y abajo. */
    <div
      ref={contenedor}
      className="relative flex items-center justify-center px-4 py-16 sm:px-6 md:py-24"
    >
      {/* Sin `overflow-hidden` acá. Recortar un elemento con transformación 3D
          obliga al navegador a recalcular el recorte en cada cuadro del scroll,
          y eso se ve como un parpadeo —del propio elemento y, si se le va la
          mano, del resto de la página, porque comparten el mismo hilo de
          pintado. La tarjeta no se sale de la sección, así que no hace falta. */}
      <div className="w-full" style={{ perspective: '1000px' }}>
        <motion.div
          style={{ translateY: subida }}
          className="mx-auto max-w-3xl text-center"
        >
          {encabezado}
        </motion.div>

        {/* Separación de verdad entre el texto y la tarjeta. Antes era `-mt-12`,
            un margen NEGATIVO copiado del componente original: la tarjeta se
            montaba 48px encima del encabezado y, como el giro 3D la pone en su
            propio plano, le tapaba la bajada. Con un margen positivo no se
            pisan nunca: el encabezado sólo se mueve hacia arriba, así que el
            hueco únicamente puede crecer. */}
        <motion.div
          style={{
            rotateX: rotacion,
            scale: escala,
            // La sombra larga y difusa es lo que hace que la tarjeta se lea
            // levantada sobre la página y no pegada al fondo.
            boxShadow:
              '0 0 #0000004d, 0 9px 20px #0000004a, 0 37px 37px #00000042, 0 84px 50px #00000026, 0 149px 60px #0000000a, 0 233px 65px #00000003',
            // Los dos de abajo son contra el parpadeo de las transformaciones
            // 3D. `willChange` le pide al navegador que ponga la tarjeta en su
            // propia capa antes de empezar a moverla, en vez de descubrirlo a
            // mitad del scroll y tener que rehacer el pintado; `backfaceVisibility`
            // evita que, mientras gira, se asome el reverso por un cuadro.
            willChange: 'transform',
            backfaceVisibility: 'hidden',
          }}
          /* `rounded-[30px]`, el mismo radio que el marco de adentro.
             No es decoración de este div: una sombra toma la forma del borde del
             elemento que la proyecta, y acá la sombra vive en ESTE div mientras
             el redondeo vivía sólo en el de adentro. O sea que detrás de un marco
             de esquinas redondeadas se dibujaban seis capas de sombra
             CUADRADAS, que asomaban por las cuatro puntas. Se veía como si la
             imagen no estuviera redondeada, cuando el problema era la sombra. */
          className="mx-auto mt-14 w-full max-w-5xl rounded-[30px]"
        >
          <Marco>{children}</Marco>
        </motion.div>
      </div>
    </div>
  );
}

/**
 * El marco oscuro alrededor de la captura: es lo que la hace leer como una
 * pantalla y no como una imagen suelta en el medio de la página.
 */
function Marco({ className = '', children }) {
  return (
    <div
      className={`h-[30rem] w-full max-w-5xl rounded-[30px] border-4 border-neutral-600 bg-neutral-800 p-2 shadow-2xl md:h-[40rem] md:p-6 ${className}`}
    >
      {/* El redondeo va TAMBIÉN en el hijo (`[&>*]:rounded-2xl`) y no sólo acá.
          Este div ya tiene `overflow-hidden rounded-2xl`, que en una caja normal
          alcanzaría para recortar la captura en las esquinas — pero el marco vive
          adentro de un elemento con `rotateX`, y el navegador rasteriza esa capa
          3D por separado: el recorte redondeado del ancestro se pierde y la
          imagen aparece con las puntas cuadradas contra un marco redondeado.

          Poniéndolo en el hijo, el redondeo es de la imagen y no depende de que
          nadie la recorte. Va como variante para que lo herede cualquier cosa que
          se le pase al marco: si mañana entra un video o un componente, no hay
          que acordarse de redondearlo.

          La captura de Home.jsx además lleva `rounded-2xl` puesto a mano, que es
          redundante a propósito: `[&>*]:rounded-2xl` es una clase arbitraria y
          Tailwind sólo la genera cuando el JIT rehace el CSS, así que en un dev
          server que ya venía corriendo no existía y las esquinas seguían
          cuadradas. La clase suelta está generada desde siempre. */}
      <div className="h-full w-full overflow-hidden rounded-2xl bg-neutral-100 [&>*]:h-full [&>*]:w-full [&>*]:rounded-2xl">
        {children}
      </div>
    </div>
  );
}
